import * as React from 'react';
import moment from 'moment';
import { compact } from 'lodash';
import classNames from 'classnames';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import CapIntroMessageButtons from 'buyside/components/presentation/MessageThreadIndexPage/MessageCard/MessageCardHelpers';
import Link from 'buyside/components/presentation/Link/link';
import ParsedHtml from 'buyside/components/presentation/ParsedHtml/parsedHtml';
import ProfileImage from 'buyside/components/presentation/ProfileImage/profileImage';

import {
  userUrl,
  jobVaultPostingUrl,
  defaultUserImageUrl,
  messageAttachmentUrl
} from 'buyside/util/urlHelper';
import { parseHtml } from 'buyside/util/textHelpers';
import { getExtensionIcon, getFilepathExtension } from 'buyside/util/fileHelper';

import {
  MessageThreadItemFragment,
  UpdateIntroRequestStatusMutationFn
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/MessageThreadIndexPage/MessageCard/MessageCardStyles.scss';

const SYSTEM_USER_ID = '9190';

interface MessageCardProps {
  updateIntroRequest: UpdateIntroRequestStatusMutationFn;
  message: MessageThreadItemFragment;
  currentUserId: string;
}

interface JobPostingDescriptionProps {
  post: MessageThreadItemFragment['jobPosting'];
}

const employerName = (user: MessageThreadItemFragment['sender']): string => {
  if (user?.currentEmployerName) {
    return user.currentEmployerName;
  } else if (user?.mostRecentEmployer?.employer) {
    return `Formerly at ${user.mostRecentEmployer.employer?.name}`;
  } else {
    return '';
  }
};

const JobPostingDescription: React.FunctionComponent<JobPostingDescriptionProps> = ({ post }) => {
  if (!post) return null;
  return (
    <div className={styles.jobPosting}>
      This message pertains to the{' '}
      <Button type={BUTTON_TYPES.LINK}>
        {({ buttonStyles }: { buttonStyles: string }): React.ReactElement => (
          <a className={buttonStyles} href={jobVaultPostingUrl(post?.id)}>
            {post?.title} position
          </a>
        )}
      </Button>{' '}
      to which you applied.
    </div>
  );
};

const MessageCard = ({
  message,
  updateIntroRequest,
  currentUserId
}: MessageCardProps): React.ReactElement => {
  let messageParagraphs = ['(No Message Body)'];
  if (message && message.body) {
    messageParagraphs = compact(parseHtml(message.body).split(/\r|\n/)).map((ele): string =>
      ele.trim()
    );
  }

  const hasSender = Boolean(message.sender);
  const senderId = message.sender?.id;
  const messageFromSystemUser = senderId === SYSTEM_USER_ID;
  const showCapIntroMessageButtons =
    message.introRequest?.status === 'pending' && senderId === message.otherParty?.id;

  return (
    <div
      className={classNames(styles.messageContainer, {
        [styles.currentUserMessage]: currentUserId === senderId
      })}
    >
      <div>
        <ProfileImage source={message.sender?.profileImageUrl || defaultUserImageUrl()} />
        <div>
          {hasSender ? (
            <>
              <Link to={userUrl(senderId)}>{message.sender?.displayName}</Link>
              <div className={styles.messageEmployer}>{employerName(message.sender)}</div>
            </>
          ) : (
            <div className={styles.displayName}>Former User</div>
          )}
        </div>
      </div>
      <div>
        {messageParagraphs.map(
          (paragraph, idx): React.ReactElement => {
            if (messageFromSystemUser || message.isCapIntroMessage) {
              return (
                <ParsedHtml key={idx} html={paragraph}>
                  {({ content }): React.ReactNode => (
                    <p className={styles.messageBody}>{content}</p>
                  )}
                </ParsedHtml>
              );
            }

            return (
              <p key={idx} className={styles.messageBody}>
                {paragraph}
              </p>
            );
          }
        )}
        {message.messageAttachments?.map(attachment => {
          const extention = getFilepathExtension(attachment.fileName);

          return (
            <Button
              icon={getExtensionIcon(extention)}
              key={`message-attachment-${attachment.id}`}
              text={attachment.customName || attachment.fileName}
              type={BUTTON_TYPES.LINK}
              url={messageAttachmentUrl(attachment.id)}
            />
          );
        })}
        {showCapIntroMessageButtons && hasSender && (
          <CapIntroMessageButtons
            employer={message.sender?.mostRecentEmployer?.employer}
            introRequest={message.introRequest}
            updateIntroRequest={updateIntroRequest}
            messageFromSystemUser={messageFromSystemUser}
          />
        )}
        <JobPostingDescription post={message.jobPosting} />
      </div>
      {message.createdAt && (
        <span className={styles.createdAt}>
          {moment(new Date(message.createdAt)).format('MMM DD, YYYY hh:mm a')}
        </span>
      )}
    </div>
  );
};

export default MessageCard;
